import {
  LOCALE_LABELS,
  SUPPORTED_LOCALES,
  type Locale,
  type Translations
} from '../../i18n/translations'

interface WebsiteSettingsOverlayProps {
  t: Translations
  status: 'disabled' | 'loading' | 'signedOut' | 'signedIn'
  userEmail?: string
  isAdmin: boolean
  locale: Locale
  setLocale: (locale: Locale) => void
  isDarkMode: boolean
  setIsDarkMode: (updater: (value: boolean) => boolean) => void
  setInfoPage: (page: 'home' | 'howto' | 'about' | 'contact' | 'terms') => void
  openInternalAction: (eventName: string) => void
  openAuth: (mode: 'signIn' | 'signUp' | 'magicLink' | 'forgotPassword' | 'resetPassword') => void
  closeOverlay: () => void
  signOut: () => void
  openHistoryEvent: string
  openAlbumSelectionEvent: string
  openHelpEvent: string
  openThemeReviewEvent: string
}

export function WebsiteSettingsOverlay({
  t,
  status,
  userEmail,
  isAdmin,
  locale,
  setLocale,
  isDarkMode,
  setIsDarkMode,
  setInfoPage,
  openInternalAction,
  openAuth,
  closeOverlay,
  signOut,
  openHistoryEvent,
  openAlbumSelectionEvent,
  openHelpEvent,
  openThemeReviewEvent
}: WebsiteSettingsOverlayProps): JSX.Element {
  if (status === 'signedIn') {
    return (
      <section className="settings-menu" role="dialog" aria-label={t.settings}>
        <div className="settings-group">
          <p className="settings-label">{t.usernameLabel}</p>
          <p className="settings-value">{userEmail ?? t.guestUser}</p>
        </div>

        <div className="settings-group">
          <label className="settings-label" htmlFor="auth-settings-language-select">
            {t.language}
          </label>
          <select
            id="auth-settings-language-select"
            className="settings-select"
            value={locale}
            onChange={(event) => setLocale(event.target.value as Locale)}
          >
            {SUPPORTED_LOCALES.map((option) => (
              <option key={option} value={option}>
                {LOCALE_LABELS[option]}
              </option>
            ))}
          </select>
        </div>

        <div className="settings-group">
          <p className="settings-label">{t.themeMode}</p>
          <button
            className="ghost-button settings-action"
            type="button"
            onClick={() => setIsDarkMode((value) => !value)}
          >
            {isDarkMode ? t.lightMode : t.darkMode}
          </button>
        </div>

        <div className="settings-group">
          <p className="settings-label">{t.quickLinks}</p>
          <button
            className="ghost-button settings-action"
            type="button"
            onClick={() => {
              setInfoPage('home')
              closeOverlay()
            }}
          >
            {t.quickLinkHome}
          </button>
          <button
            className="ghost-button settings-action"
            type="button"
            onClick={() => openInternalAction(openHistoryEvent)}
          >
            {t.quickLinkInvestments}
          </button>
          <button
            className="ghost-button settings-action"
            type="button"
            onClick={() => openInternalAction(openAlbumSelectionEvent)}
          >
            {t.quickLinkAlbumSelection}
          </button>
          {isAdmin && (
            <button
              className="ghost-button settings-action"
              type="button"
              onClick={() => openInternalAction(openThemeReviewEvent)}
            >
              {t.quickLinkThemeReview}
            </button>
          )}
          <button
            className="ghost-button settings-action"
            type="button"
            onClick={() => openInternalAction(openHelpEvent)}
          >
            {t.quickLinkHelp}
          </button>
          <button
            className="ghost-button settings-action"
            type="button"
            onClick={() => {
              setInfoPage('terms')
              closeOverlay()
            }}
          >
            {t.publicTerms}
          </button>
          <button
            className="ghost-button settings-action"
            type="button"
            onClick={() => {
              signOut()
              closeOverlay()
            }}
          >
            {t.authSignOut}
          </button>
        </div>
      </section>
    )
  }

  return (
    <section className="settings-menu" role="dialog" aria-label={t.settings}>
      <div className="settings-group">
        <label className="settings-label" htmlFor="auth-settings-language-select-signed-out">
          {t.language}
        </label>
        <select
          id="auth-settings-language-select-signed-out"
          className="settings-select"
          value={locale}
          onChange={(event) => setLocale(event.target.value as Locale)}
        >
          {SUPPORTED_LOCALES.map((option) => (
            <option key={option} value={option}>
              {LOCALE_LABELS[option]}
            </option>
          ))}
        </select>
      </div>

      <div className="settings-group">
        <button
          type="button"
          className="ghost-button settings-action"
          onClick={() => {
            setInfoPage('terms')
            closeOverlay()
          }}
        >
          {t.publicTerms}
        </button>
      </div>

      <div className="settings-group">
        <p className="settings-value">login to see your albums</p>
        <button
          type="button"
          className="auth-link settings-link"
          onClick={() => openAuth('signIn')}
        >
          {t.authSignIn}
        </button>
      </div>
    </section>
  )
}
